import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Globe, Database, UserCheck, Clock, FileText, MapPin, Share2, Users, Eye, Mail } from 'lucide-react';

const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

export function PrivacyPolicyPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleContactClick = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate('/');
        setTimeout(() => {
            const section = document.getElementById('contact-section');
            if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
            }
        }, 150);
    };

    const sections = [
        { key: 'controller', icon: Shield },
        { key: 'dataCollected', icon: Database },
        { key: 'sharing', icon: Share2 },
        { key: 'international', icon: Globe },
        { key: 'retention', icon: Clock },
        { key: 'rights', icon: UserCheck },
        { key: 'minors', icon: Users },
        { key: 'publicData', icon: Eye },
        { key: 'geolocation', icon: MapPin },
        { key: 'changes', icon: FileText }
    ];

    return (
        <article className="pb-10">
            {/* Header */}
            <header className="mb-14 text-center">
                <div className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-700 dark:text-brand-300 text-sm font-medium mb-6">
                    <Lock size={16} />
                    <span>{t('legal.badge', 'Conformidade Legal')}</span>
                </div>
                <h1>{t('legal.privacy.title')}</h1>
                <p className="text-muted-foreground text-lg sm:text-[1.1rem] leading-relaxed mt-6 text-justify">
                    {t('legal.privacy.introduction')}
                </p>
                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground/80 bg-background/50 inline-flex px-4 py-2 rounded-lg border border-border/50">
                    <Clock size={16} />
                    <span className='text-left'>{t('legal.privacy.lastUpdated')}</span>
                </div>
            </header>

            {/* Content Sections */}
            <div className="space-y-16">
                {sections.map(({ key, icon: Icon }) => (
                    <motion.section
                        key={key}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeInUp}
                        className="scroll-mt-32 relative"
                    >
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-brand-500/10 to-transparent flex items-center justify-center text-brand-600 dark:text-brand-400 border border-brand-500/20 shadow-sm">
                                <Icon size={22} strokeWidth={1.5} />
                            </div>
                            <h2 className="!my-0 !border-none !pb-0 flex-1 text-2xl sm:text-[1.65rem] leading-tight mt-1.5">{t(`legal.privacy.sections.${key}.title`)}</h2>
                        </div>

                        <div
                            className="prose-p:mt-0 pt-2 prose-p:text-justify text-justify text-muted-foreground text-[1.05rem] leading-relaxed pl-0 md:pl-16"
                            dangerouslySetInnerHTML={{
                                __html: t(`legal.privacy.sections.${key}.content`).replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
                            }}
                        />

                        {/* Special case for Data Collected table */}
                        {key === 'dataCollected' && (
                            <div className="mt-8 md:ml-16 overflow-hidden rounded-2xl border border-border/50 bg-background/50 backdrop-blur-sm shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse min-w-[700px] !my-0">
                                        <thead>
                                            <tr className="bg-muted/50 border-b border-border/50">
                                                {(t('legal.privacy.sections.dataCollected.tableHeaders', { returnObjects: true }) as string[])?.map((header: string, i: number) => (
                                                    <th key={i} className="px-6 py-4 text-sm font-semibold text-foreground/90 whitespace-nowrap !border-none">
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/50">
                                            {(t('legal.privacy.sections.dataCollected.tableRows', { returnObjects: true }) as string[][])?.map((row: string[], i: number) => (
                                                <tr key={i} className="hover:bg-muted/30 transition-colors group">
                                                    <td className="px-6 py-5 text-sm font-medium text-foreground/90 !border-none">{row[0]}</td>
                                                    <td className="px-6 py-5 text-sm text-muted-foreground !border-none">{row[1]}</td>
                                                    <td className="px-6 py-5 text-sm text-muted-foreground !border-none">{row[2]}</td>
                                                    <td className="px-6 py-5 text-sm text-muted-foreground !border-none">
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-brand-500/10 text-brand-700 dark:text-brand-300 text-xs font-medium group-hover:bg-brand-500/20 transition-colors">
                                                            {row[3]}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </motion.section>
                ))}
            </div>

            {/* Contact Footer Banner */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="mt-20 p-8 sm:p-10 rounded-[2rem] bg-card/50 dark:bg-[#1C1714]/80 backdrop-blur-sm border border-border/60 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden group"
            >
                {/* Decorative background glow */}
                <div className="absolute top-0 right-0 -mr-8 -mt-8 w-64 h-64 rounded-full bg-brand-500/5 blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 -ml-8 -mb-8 w-64 h-64 rounded-full bg-brand-500/5 blur-3xl pointer-events-none" />

                <div className="text-left relative z-10 flex-1 w-full">
                    <h3 className="!my-0 text-2xl font-display font-semibold mb-2">{t('legal.privacy.contact.title', 'Ainda tem dúvidas sobre seus dados?')}</h3>
                    <p className="text-muted-foreground !my-0 text-[1.1rem]">{t('legal.privacy.contact.description', 'Nossa equipe de suporte está pronta para ajudar.')}</p>
                </div>
                <button
                    onClick={handleContactClick}
                    className="!text-white !no-underline transition-all duration-300 bg-[#6F4E37] hover:bg-[#5A3E2A] px-8 py-4 rounded-xl font-semibold shadow flex items-center justify-center gap-2 whitespace-nowrap shrink-0 w-full md:w-auto relative z-10 active:scale-[0.98]"
                >
                    <Mail size={18} />
                    {t('legal.privacy.contact.button', 'Entrar em contato')}
                </button>
            </motion.div>
        </article>
    );
}
